// components/ui/ImageCropDialog.tsx
import React, { useState } from "react";
import { Dialog } from "@headlessui/react";
import Cropper from "react-easy-crop";
import { RotateCcw, RotateCw } from "lucide-react";
import { getCroppedImg } from "../../utils/cropImage";
import { useTranslation } from "react-i18next";

interface ImageCropDialogProps {
  src: string; // Image URL to crop
  aspect?: number;
  onClose: () => void;
  onSave: (croppedFile: File) => void;
}

const ImageCropDialog: React.FC<ImageCropDialogProps> = ({ src, aspect = 1, onClose, onSave }) => {
  const {t} = useTranslation();
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<any>(null);

  const onCropComplete = (_: any, croppedPixels: any) => setCroppedAreaPixels(croppedPixels);

  const handleSave = async () => {
    if (!croppedAreaPixels) return;
    const croppedFile = await getCroppedImg(src, croppedAreaPixels, rotation);
    onSave(croppedFile);
  };

  return (
    <Dialog
      open={true}
      onClose={onClose}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
    >
      <div className="bg-white p-4 rounded w-96 h-96 flex flex-col">
        <div className="relative flex-1">
          <Cropper
            image={src}
            crop={crop}
            zoom={zoom}
            rotation={rotation}
            aspect={aspect}
            onCropChange={setCrop}
            onZoomChange={setZoom}
            onRotationChange={setRotation}
            onCropComplete={onCropComplete}
          />
        </div>

        <div className="flex items-center justify-between mt-2 gap-2">
          <div className="flex items-center gap-2">
            <button
              type="button"
              className="p-1 bg-gray-200 rounded"
              onClick={() => setRotation((r) => r - 90)}
            >
              <RotateCcw size={16} />
            </button>
            <button
              type="button"
              className="p-1 bg-gray-200 rounded"
              onClick={() => setRotation((r) => r + 90)}
            >
              <RotateCw size={16} />
            </button>
          </div>
          <input
            type="range"
            min={1}
            max={3}
            step={0.01}
            value={zoom}
            onChange={(e) => setZoom(Number(e.target.value))}
            className="flex-1"
          />
          <div className="flex gap-2">
            <button className="px-4 py-2 bg-gray-200 rounded capitalize" onClick={onClose}>
              {t("cancel")}
            </button>
            <button className="px-4 py-2 bg-primary text-white rounded capitalize" onClick={handleSave}>
              {t("save")}
            </button>
          </div>
        </div>
      </div>
    </Dialog>
  );
};

export default ImageCropDialog;
